import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../services/supabase/AuthContext';
import { useSync } from '../services/sync/useSync';

// A compact AppBar control that surfaces sync state and exposes a manual
// refresh. Renders `null` when signed out or Supabase isn't configured so
// the offline-only path keeps a clean toolbar.
export default function SyncStatusBadge() {
  const { session, isConfigured } = useAuth();
  const { status, lastSyncAt, lastError, triggerSync } = useSync();

  if (!isConfigured || !session) return null;

  const tooltipParts: string[] = [];
  if (status === 'syncing') {
    tooltipParts.push('Syncing…');
  } else if (status === 'error') {
    tooltipParts.push(lastError ?? 'Sync failed');
  } else {
    tooltipParts.push('Synced');
  }
  if (lastSyncAt) {
    tooltipParts.push(
      `Last sync ${formatDistanceToNow(lastSyncAt, { addSuffix: true })}`,
    );
  }
  const tooltip = tooltipParts.join(' • ');

  const ariaLabel =
    status === 'syncing'
      ? 'Syncing in progress'
      : status === 'error'
        ? 'Sync error — click to retry'
        : 'Sync now';

  return (
    <Tooltip title={tooltip}>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <IconButton
          color="inherit"
          size="small"
          onClick={triggerSync}
          aria-label={ariaLabel}
          disabled={status === 'syncing'}
        >
          {status === 'syncing' ? (
            <CircularProgress size={18} color="inherit" />
          ) : status === 'error' ? (
            <CloudOffIcon fontSize="small" />
          ) : lastSyncAt ? (
            <CloudDoneIcon fontSize="small" />
          ) : (
            <RefreshIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
    </Tooltip>
  );
}
